import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { GitCompare, TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, LineChart, Line, CartesianGrid } from 'recharts';

export default function PnLComparePage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [periods, setPeriods] = useState(6);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/accounting/profit-loss/compare?periods=${periods}`);
      setData(res.data);
    } catch { toast.error('Failed to load comparison'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const TrendIcon = ({ value }) => {
    if (value > 0) return <ArrowUp size={14} className="text-success" />;
    if (value < 0) return <ArrowDown size={14} className="text-error" />;
    return <Minus size={14} className="text-muted-foreground" />;
  };

  const chartData = data?.periods?.map(p => ({
    month: p.month,
    Revenue: p.revenue,
    Expenses: p.operating_expenses + p.cost_of_sales,
    'Net Profit': p.net_profit,
  })) || [];

  const marginData = data?.periods?.map(p => ({
    month: p.month,
    'Gross Margin %': p.gross_margin,
    'Net Margin %': p.net_margin,
  })) || [];

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="pnl-compare-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200/50">
              <GitCompare size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">
                {t('pnl_compare_title') || 'P&L Comparison'}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">Compare revenue, expenses, and profit over time</p>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            {[3, 6, 12].map(p => (
              <Button key={p} size="sm" variant={periods === p ? 'default' : 'outline'} onClick={() => { setPeriods(p); }}
                data-testid={`pnl-period-${p}`}>{p} Months</Button>
            ))}
            <Button size="sm" onClick={fetchData} data-testid="pnl-generate-btn">Generate</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data && (
          <>
            {/* Revenue vs Expenses Chart */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Revenue vs Expenses</CardTitle></CardHeader>
              <CardContent>
                <div className="h-64" data-testid="pnl-bar-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Revenue" fill="#10b981" />
                      <Bar dataKey="Expenses" fill="#ef4444" />
                      <Bar dataKey="Net Profit" fill="#6366f1" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Margin Trends */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Margin Trends</CardTitle></CardHeader>
              <CardContent>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={marginData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis unit="%" />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="Gross Margin %" stroke="#10b981" strokeWidth={2} />
                      <Line type="monotone" dataKey="Net Margin %" stroke="#6366f1" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Detail Table */}
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm" data-testid="pnl-compare-table">
                  <thead>
                    <tr className="border-b bg-muted/50 dark:bg-foreground text-muted-foreground">
                      <th className="text-left p-3 font-medium">Metric</th>
                      {data.periods.map(p => <th key={p.month} className="text-right p-3 font-medium">{p.month}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Revenue', key: 'revenue', color: 'text-success' },
                      { label: 'Cost of Sales', key: 'cost_of_sales', color: 'text-error' },
                      { label: 'Gross Profit', key: 'gross_profit', color: 'text-info', bold: true },
                      { label: 'Operating Expenses', key: 'operating_expenses', color: 'text-error' },
                      { label: 'Net Profit', key: 'net_profit', color: 'text-violet-700', bold: true },
                      { label: 'Gross Margin %', key: 'gross_margin', color: 'text-muted-foreground' },
                      { label: 'Net Margin %', key: 'net_margin', color: 'text-muted-foreground' },
                      { label: 'Sales Count', key: 'sales_count', color: 'text-muted-foreground' },
                    ].map(row => (
                      <tr key={row.key} className={`border-b ${row.bold ? 'bg-muted/50 font-bold' : ''}`}>
                        <td className="p-3">{row.label}</td>
                        {data.periods.map(p => (
                          <td key={p.month} className={`p-3 text-right font-mono ${row.color}`}>
                            <span className="flex items-center justify-end gap-1">
                              {row.key.includes('margin') ? `${p[row.key]}%` : p[row.key]?.toLocaleString()}
                              {p.revenue_change !== undefined && row.key === 'revenue' && <TrendIcon value={p.revenue_change} />}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
